import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { Badge, ACCOUNT_LABEL } from "@/pages/admin/AdminCompanies";

/**
 * Denetim Kaydı — admin_audit_log okuma sayfası.
 *
 * Backend ``GET /api/admin/audit-log`` çağrılır; kayıtlar newest-first
 * gelir.  Bu sayfa yalnızca OKUMA yapar; audit'e yeni kayıt eklemez ve
 * mevcut kayıtları değiştirmez.
 */

// Backend action → Türkçe okunabilir etiket.  Yeni bir action türü
// eklendiğinde bu sözlüğe kayıt düşer; sözlükte olmayan action ham
// olarak gösterilir (böylece geleceğe dönük audit tipleri kaybolmaz).
const ACTION_LABEL = {
  "company.archive": "Arşivlendi",
  "company.soft_delete": "Silinmişlere Taşındı",
  "company.restore": "Geri Alındı", // ince alt kırılım aşağıda meta.to'ya göre
  "company.permanent_delete.attempt": "Kalıcı Silme Denemesi",
};

const ACTION_FILTER_OPTIONS = [
  { key: "all", label: "Tümü" },
  { key: "company.archive", label: "Arşivlendi" },
  { key: "company.soft_delete", label: "Silinmişlere Taşındı" },
  { key: "company.restore", label: "Geri Alındı" },
  { key: "company.permanent_delete.attempt", label: "Kalıcı Silme Denemesi" },
];

// Restore'ta yeni durum meta.to'da yer alır; kullanıcıya daha spesifik
// bir başlık göstermek için alt ayrım.
function actionLabel(row) {
  const a = row?.action;
  if (a === "company.restore") {
    const to = row?.meta?.to;
    if (to === "archived") return "Silinmişlerden Geri Alındı";
    if (to === "passive")  return "Arşivden Geri Alındı";
    return "Geri Alındı";
  }
  return ACTION_LABEL[a] || a || "—";
}

// Audit satırından eski/yeni durumları çıkar.  Backend audit
// kayıtlarında:
//   company.archive:               meta.from (=passive) → archived
//   company.soft_delete:           meta.from (=archived) → deleted
//   company.restore:               meta.from → meta.to
//   company.permanent_delete.attempt: durum değişmez, "—" gösterilir
function resolveTransition(row) {
  const a = row?.action;
  const from = row?.meta?.from || null;
  const to = row?.meta?.to || null;
  if (a === "company.archive")     return { from, to: "archived" };
  if (a === "company.soft_delete") return { from, to: "deleted" };
  if (a === "company.restore")     return { from, to };
  return { from: null, to: null };
}

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (filter && filter !== "all") params.action = filter;
      const { data } = await api.get("/admin/audit-log", { params });
      setRows(data || []);
    } catch {
      toast.error("Denetim kaydı yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => { load(); }, [filter, load]);

  const onSearchKey = (e) => { if (e.key === "Enter") load(); };
  const clearFilters = () => {
    setSearch("");
    setFilter("all");
  };

  const list = useMemo(() => rows || [], [rows]);
  const hasActiveFilters = search.trim() !== "" || filter !== "all";

  return (
    <div className="space-y-6" data-testid="admin-audit-log-page">
      <PageHeader
        title="Denetim Kaydı"
        subtitle="Firma hesaplarında gerçekleştirilen yönetim işlemlerinin geçmişi."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Firma adı ara... (Enter)"
            className="pl-9 h-10 rounded-lg"
            data-testid="admin-audit-search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-64 h-10 rounded-lg" data-testid="admin-audit-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTION_FILTER_OPTIONS.map((f) => (
              <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-10 rounded-lg text-slate-600 hover:text-slate-900"
            data-testid="admin-audit-clear-filters"
          >
            <X className="w-4 h-4 mr-1" /> Temizle
          </Button>
        )}
      </div>

      <SectionCard>
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Yükleniyor…</div>
        ) : list.length === 0 ? (
          <EmptyState title="Henüz denetim kaydı bulunmuyor." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Firma</th>
                  <th className="px-5 py-3 font-semibold">İşlem</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Eski Durum</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Yeni Durum</th>
                  <th className="px-5 py-3 font-semibold">İşlemi Yapan</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Tarih / Saat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((r) => {
                  const { from, to } = resolveTransition(r);
                  const missingCompany = !r.company_name;
                  return (
                    <tr
                      key={r.id}
                      className="hover:bg-slate-50/60"
                      data-testid={`admin-audit-row-${r.id}`}
                    >
                      <td className="px-5 py-3">
                        {missingCompany ? (
                          <div>
                            <div className="text-slate-500 italic">Firma bulunamadı</div>
                            {r.company_id && (
                              <div className="text-[11px] text-slate-400 truncate max-w-[220px]">
                                {r.company_id}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="font-medium text-slate-900">{r.company_name}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{actionLabel(r)}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {from
                          ? <Badge tone={ACCOUNT_LABEL[from]} fallback={from} />
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {to
                          ? <Badge tone={ACCOUNT_LABEL[to]} fallback={to} />
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-5 py-3 text-slate-700">
                        {r.actor_username || r.actor_admin_id || "—"}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                        {r.at ? fmtDateTime(r.at) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
